import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@apollo/client';
import { Button, Input, List, Tag, Popconfirm, Empty, message, Flex, Tooltip } from 'antd';

import { FontAwesomeIcon } from 'components/Utils/FontAwesomeIconWrapper';
import { faUserPlus, faTrash, faEye } from '@fortawesome/pro-light-svg-icons';

import { projectMembers } from 'graph/queries';
import {
	useAddProjectMemberMutation,
	useRemoveProjectMemberMutation
} from 'graph/dist/generated';

// Onglet « Partage » de la modale projet (owner only). Liste les lecteurs du
// projet et permet d'inviter par email / révoquer. Le partage est en lecture
// seule (role reader) — l'accès est effectif dès que l'invité se connecte avec
// cet email (cf backend : access map + email du claim OIDC).
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export const ProjectShareTab: React.FC<{ projectId: string }> = ({ projectId }) => {
	const { t, i18n } = useTranslation();
	const [email, setEmail] = useState('');

	const { data, loading, refetch } = useQuery(projectMembers, {
		variables: { id: projectId },
		skip: !projectId,
		fetchPolicy: 'cache-and-network'
	});

	const [addMember, { loading: adding }] = useAddProjectMemberMutation();
	const [removeMember] = useRemoveProjectMemberMutation();

	const members = (data?.getProject?.members || []).filter(Boolean);

	const onInvite = async () => {
		if (adding) return;
		const value = email.trim().toLowerCase();
		if (!EMAIL_RE.test(value)) {
			message.error(t('models.project.share.invalid_email', { defaultValue: 'Adresse email invalide' }));
			return;
		}
		if (members.some((m: any) => (m.invitedEmail || '').toLowerCase() === value)) {
			message.info(t('models.project.share.already_member', { defaultValue: 'Cette personne a déjà accès' }));
			return;
		}
		try {
			await addMember({ variables: { projectId, email: value, role: 'reader', locale: i18n.language || 'en' } });
			setEmail('');
			message.success(t('models.project.share.invited', { defaultValue: 'Invitation envoyée' }));
		} catch (e: any) {
			message.error(e?.message || t('models.project.share.invite_failed', { defaultValue: "Échec de l'invitation" }));
			return;
		}
		// refetch séparé : son échec éventuel ne doit pas afficher une erreur d'invitation
		try { await refetch(); } catch {}
	};

	const onRemove = async (id: string) => {
		try {
			await removeMember({ variables: { id } });
			await refetch();
			message.success(t('models.project.share.revoked', { defaultValue: 'Accès révoqué' }));
		} catch (e: any) {
			message.error(e?.message || t('models.project.share.revoke_failed', { defaultValue: 'Échec de la révocation' }));
		}
	};

	return (
		<div className="ProjectShareTab">
			<p className="text-secondary">
				{t('models.project.share.description', {
					defaultValue: 'Invitez des personnes à consulter ce projet en lecture seule. Elles ne pourront rien modifier ni générer.'
				})}
			</p>

			<Flex gap={8} className="mb-2">
				<Input
					type="email"
					placeholder={t('models.project.share.email_placeholder', { defaultValue: 'email@exemple.com' })}
					value={email}
					onChange={(e) => setEmail(e.target.value)}
					onPressEnter={onInvite}
					allowClear
				/>
				<Button type="primary" onClick={onInvite} loading={adding} disabled={!email.trim()}>
					{/* @ts-ignore */}
					<FontAwesomeIcon icon={faUserPlus} style={{ marginRight: 8 }} />
					{t('models.project.share.invite', { defaultValue: 'Inviter' })}
				</Button>
			</Flex>

			{members.length === 0 ? (
				<Empty
					image={Empty.PRESENTED_IMAGE_SIMPLE}
					description={t('models.project.share.no_members', { defaultValue: 'Aucun lecteur pour le moment' })}
				/>
			) : (
				<List
					size="small"
					loading={loading && members.length === 0}
					dataSource={members}
					renderItem={(m: any) => (
						<List.Item
							actions={[
								<Popconfirm
									key="remove"
									title={t('models.project.share.revoke_confirm', { defaultValue: 'Révoquer cet accès ?' })}
									okText={t('actions.confirm', { defaultValue: 'Oui' })}
									cancelText={t('actions.cancel', { defaultValue: 'Annuler' })}
									onConfirm={() => onRemove(m.id)}
								>
									<Button type="text" danger size="small">
										{/* @ts-ignore */}
										<FontAwesomeIcon icon={faTrash} />
									</Button>
								</Popconfirm>
							]}
						>
							<List.Item.Meta
								avatar={
									<Tooltip title={t('models.project.share.role_reader', { defaultValue: 'Lecteur' })}>
										{/* @ts-ignore */}
										<FontAwesomeIcon icon={faEye} />
									</Tooltip>
								}
								title={m.invitedEmail}
								description={
									<Tag color="blue" bordered={false}>
										{t('models.project.share.role_reader', { defaultValue: 'Lecteur' })}
									</Tag>
								}
							/>
						</List.Item>
					)}
				/>
			)}
		</div>
	);
};

export default ProjectShareTab;
